import React, { useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  LayoutDashboard,
  Users,
  FileText,
  Edit3,
  ArrowLeft,
  Zap,
  CreditCard,
  Tag,
} from "lucide-react";

const NAV = [
  { to: "/admin",          label: "DASHBOARD", Icon: LayoutDashboard, end: true,  testid: "admin-nav-dashboard" },
  { to: "/admin/users",    label: "USERS",     Icon: Users,                       testid: "admin-nav-users" },
  { to: "/admin/results",  label: "RESULTS",   Icon: FileText,                    testid: "admin-nav-results" },
  { to: "/admin/payments", label: "PAYMENTS",  Icon: CreditCard,                  testid: "admin-nav-payments" },
  { to: "/admin/pricing",  label: "PRICING",   Icon: Tag,                         testid: "admin-nav-pricing" },
  { to: "/admin/content",  label: "CONTENT",   Icon: Edit3,                       testid: "admin-nav-content" },
];

export default function AdminLayout() {
  const { user, loading } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) {
      nav("/login?next=/admin");
      return;
    }
    if (user.role !== "admin") {
      nav("/");
    }
  }, [user, loading, nav]);

  if (loading || !user || user.role !== "admin") {
    return (
      <div className="min-h-screen bg-[#070B14] flex items-center justify-center">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#64748B]">
          // Verifying access…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070B14] text-white flex">
      {/* Sidebar */}
      <aside className="w-60 border-r border-[#1E2A3F] bg-[#0A0E1A] flex flex-col">
        <div className="px-5 py-5 border-b border-[#1E2A3F]">
          <div className="flex items-center gap-2 mb-1">
            <div className="h-7 w-7 bg-gradient-to-br from-[#3B82F6] to-[#8B5CF6] flex items-center justify-center rounded-md">
              <Zap className="h-4 w-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-lg tracking-tight" style={{ fontWeight: 800 }}>
              Admin
            </span>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
            IQ_LOG · CONSOLE
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] rounded-md transition-colors ${
                  isActive
                    ? "bg-[#3B82F6]/10 text-[#3B82F6] border border-[#3B82F6]/40"
                    : "text-[#94A3B8] hover:text-white hover:bg-[#0F1525] border border-transparent"
                }`
              }
            >
              <n.Icon className="h-4 w-4" strokeWidth={1.75} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#1E2A3F]">
          <button
            onClick={() => nav("/")}
            data-testid="admin-back-home"
            className="w-full flex items-center gap-2 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] hover:text-white"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} />
            Back to site
          </button>
          <div className="mt-3 px-3 py-2 bg-[#0F1525] border border-[#1E2A3F] rounded-md">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
              Logged in as
            </div>
            <div className="text-xs text-white truncate mt-1">{user.email}</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#3B82F6] mt-1">
              ⚡ {user.role}
            </div>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-x-auto">
        <Outlet />
      </main>
    </div>
  );
}
